import { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Search, Download, Eye, Loader2, ChevronLeft, ChevronRight, Mail, LineChart as LineChartIcon } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { fmtDateTime } from '../../lib/utils';
import { HtmlView } from '../RichTextEditor';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const PAGE = 15;

const STATUS_STYLE = {
  draft: 'bg-slate-100 text-slate-600', pending_approval: 'bg-amber-100 text-amber-700',
  approved: 'bg-sky-100 text-sky-700', scheduled: 'bg-indigo-100 text-indigo-700',
  sending: 'bg-purple-100 text-purple-700', sent: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-red-100 text-red-600',
};

export default function SentCampaignsTab({ meta }) {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('sent');
  const [sort, setSort] = useState('sent_at:desc');
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    const [sort_by, sort_dir] = sort.split(':');
    api.get('/comms/campaigns-search', { params: { q, status: status === '_all' ? '' : status, skip: page * PAGE, limit: PAGE, sort_by, sort_dir } })
      .then((r) => setData(r.data)).catch(() => {});
  }, [q, status, sort, page]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE)) : 1;

  return (
    <div className="space-y-4" data-testid="sent-campaigns-tab">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search by name, subject or sender…" value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} className="pl-9 h-9" data-testid="sent-search" />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(0); }}>
          <SelectTrigger className="h-9 w-44 text-xs" data-testid="sent-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="_all">All Statuses</SelectItem>
            {['draft', 'pending_approval', 'approved', 'scheduled', 'sending', 'failed'].map((s) => (
              <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={(v) => { setSort(v); setPage(0); }}>
          <SelectTrigger className="h-9 w-44 text-xs" data-testid="sent-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="sent_at:desc">Newest first</SelectItem>
            <SelectItem value="sent_at:asc">Oldest first</SelectItem>
            <SelectItem value="name:asc">Name A–Z</SelectItem>
            <SelectItem value="recipient_count:desc">Most recipients</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {!data ? <p className="text-sm text-slate-400 text-center py-8">Loading…</p> : data.items.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-10">No campaigns match</p>
      ) : (
        <div className="space-y-2">
          {data.items.map((c) => {
            const st = c.stats || {};
            const sent = st.sent || c.recipient_count || 0;
            const pct = (n) => (sent ? `${Math.round((n || 0) / sent * 100)}%` : '0%');
            return (
              <div key={c.id} className="border border-slate-200 rounded-xl p-4 bg-white flex flex-wrap items-center gap-3 hover:border-magenta/50 transition" data-testid={`sent-row-${c.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-navy">{c.name}</span>
                    <Badge className={`${STATUS_STYLE[c.status] || ''} border-0 text-[10px] uppercase`}>{c.status.replace('_', ' ')}</Badge>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 truncate">{c.subject || 'No subject'} · by {c.created_by_name}{c.sent_at && ` · ${fmtDateTime(c.sent_at)}`}</div>
                  {c.status === 'sent' && (
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-[11px] text-slate-600">
                      <span><b className="text-navy">{c.recipient_count}</b> recipients</span>
                      <span><b className="text-emerald-600">{st.delivered || st.sent || 0}</b> delivered</span>
                      <span><b className="text-navy">{st.opened || 0}</b> opened ({pct(st.opened)})</span>
                      <span><b className="text-navy">{st.clicked || 0}</b> clicked ({pct(st.clicked)})</span>
                      <span><b className="text-rose-500">{st.bounced || 0}</b> bounced</span>
                      <span><b className="text-slate-500">{st.unsubscribed || 0}</b> unsubscribed</span>
                    </div>
                  )}
                </div>
                <Button size="sm" variant="outline" onClick={() => setDetail(c)} data-testid={`sent-view-${c.id}`}>
                  <Eye className="w-3.5 h-3.5 mr-1.5" /> View Details
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {data && data.total > PAGE && (
        <div className="flex items-center justify-center gap-3 text-sm" data-testid="sent-pagination">
          <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="sent-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-slate-500">Page {page + 1} of {totalPages}</span>
          <Button size="sm" variant="outline" disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)} data-testid="sent-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      )}

      {detail && <CampaignDetailDialog campaign={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function CampaignDetailDialog({ campaign, onClose }) {
  const [report, setReport] = useState(null);
  const [recips, setRecips] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [cumulative, setCumulative] = useState(true);
  const [rq, setRq] = useState('');
  const [rpage, setRpage] = useState(0);

  useEffect(() => {
    api.get(`/comms/campaigns/${campaign.id}/report`).then((r) => setReport(r.data)).catch(() => {});
    api.get(`/comms/campaigns/${campaign.id}/timeline`).then((r) => setTimeline(r.data)).catch(() => {});
  }, [campaign.id]);

  useEffect(() => {
    api.get(`/comms/campaigns/${campaign.id}/recipients`, { params: { skip: rpage * 25, limit: 25 } })
      .then((r) => setRecips(r.data)).catch(() => {});
  }, [campaign.id, rpage]);

  const exportCsv = async () => {
    const token = localStorage.getItem('aawta_token');
    try {
      const resp = await fetch(`${API_BASE}/api/comms/campaigns/${campaign.id}/export.csv`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `campaign-${campaign.name}-results.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('Export started');
    } catch { toast.error('Export failed'); }
  };

  const c = report?.campaign || campaign;
  const counts = report?.counts || {};
  const rates = report?.rates || {};
  const filteredRecips = (recips?.items || []).filter((r) => !rq || r.email.includes(rq.toLowerCase()));

  const statCards = [
    ['Recipients', report?.recipients_total ?? c.recipient_count, ''],
    ['Delivered', counts.delivered ?? '—', `${rates.delivery_rate ?? 0}%`],
    ['Opened', counts.opened ?? '—', `${rates.open_rate ?? 0}% open rate`],
    ['Clicked', counts.clicked ?? '—', `${rates.click_rate ?? 0}% CTR`],
    ['Bounced', counts.bounced ?? '—', `${rates.bounce_rate ?? 0}%`],
    ['Unsubscribed', counts.unsubscribed ?? '—', `${rates.unsubscribe_rate ?? 0}%`],
    ['Failed', counts.failed ?? '—', `${rates.failure_rate ?? 0}%`],
  ];

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="campaign-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-8"><Mail className="w-4 h-4 text-magenta shrink-0" /><span className="truncate">{c.name}</span></DialogTitle>
        </DialogHeader>

        <div className="text-sm space-y-1">
          <div><span className="text-slate-500">Subject:</span> <span className="font-medium text-navy">{c.subject || '—'}</span></div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500">
            <span>By {c.created_by_name}</span>
            {c.sent_at && <span>Sent {fmtDateTime(c.sent_at)}</span>}
            <span className="capitalize">Status: {c.status?.replace('_', ' ')}</span>
            {c.approved_by_name && <span>Approved by {c.approved_by_name}</span>}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="campaign-stat-cards">
          {statCards.map(([label, val, sub]) => (
            <div key={label} className="bg-slate-50 rounded-lg p-2.5">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
              <div className="font-heading text-lg font-bold text-navy">{report ? val : <Loader2 className="w-3.5 h-3.5 animate-spin" />}</div>
              {sub && <div className="text-[10px] text-slate-400">{sub}</div>}
            </div>
          ))}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1.5">
              <LineChartIcon className="w-3.5 h-3.5 text-magenta" /> Performance Over Time
              {timeline && <span className="normal-case font-normal text-slate-400">(per {timeline.granularity})</span>}
            </div>
            {timeline?.buckets?.length > 0 && (
              <div className="flex rounded-md border border-slate-200 overflow-hidden text-[10px] font-semibold">
                <button onClick={() => setCumulative(true)} className={`px-2.5 py-1 ${cumulative ? 'bg-navy text-white' : 'bg-white text-slate-500'}`} data-testid="timeline-cumulative">Cumulative</button>
                <button onClick={() => setCumulative(false)} className={`px-2.5 py-1 ${!cumulative ? 'bg-navy text-white' : 'bg-white text-slate-500'}`} data-testid="timeline-per-bucket">Per {timeline.granularity}</button>
              </div>
            )}
          </div>
          {!timeline ? (
            <div className="h-24 flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin text-slate-300" /></div>
          ) : timeline.buckets.length === 0 ? (
            <p className="text-xs text-slate-400 py-4 text-center" data-testid="timeline-empty">No send data yet for this campaign.</p>
          ) : (
            <div data-testid="campaign-timeline-chart">
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={timeline.buckets}>
                  <defs>
                    <linearGradient id="gOpen" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#E91E8C" stopOpacity={0.35} /><stop offset="100%" stopColor="#E91E8C" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="gClick" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0E1944" stopOpacity={0.35} /><stop offset="100%" stopColor="#0E1944" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="t" stroke="#94A3B8" fontSize={9} tickFormatter={(v) => v.slice(5, timeline.granularity === 'hour' ? 16 : 10)} />
                  <YAxis stroke="#94A3B8" fontSize={10} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #E2E8F0', fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey={cumulative ? 'cum_opened' : 'opened'} name="Opens" stroke="#E91E8C" strokeWidth={2.5} fill="url(#gOpen)" />
                  <Area type="monotone" dataKey={cumulative ? 'cum_clicked' : 'clicked'} name="Clicks" stroke="#0E1944" strokeWidth={2.5} fill="url(#gClick)" />
                </AreaChart>
              </ResponsiveContainer>
              {!timeline.has_events && (
                <p className="text-[10px] text-amber-600 mt-1" data-testid="timeline-webhook-hint">
                  Send times only — open/click points appear once the Resend webhook is configured for your domain.
                </p>
              )}
            </div>
          )}
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Email Content</div>
          <div className="border border-slate-200 rounded-lg p-4 max-h-72 overflow-y-auto bg-white" data-testid="campaign-content">
            <HtmlView html={c.html_body} className="text-sm" />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Recipients {recips ? `(${recips.total})` : ''}</div>
            <div className="flex gap-2">
              <Input placeholder="Filter by email…" value={rq} onChange={(e) => setRq(e.target.value)} className="h-8 w-48 text-xs" data-testid="recips-filter" />
              <Button size="sm" variant="outline" onClick={exportCsv} data-testid="campaign-export-csv"><Download className="w-3.5 h-3.5 mr-1" /> CSV</Button>
            </div>
          </div>
          <div className="border border-slate-100 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-slate-500">Email</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-500">Name</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-500">Status</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-500 hidden sm:table-cell">Events</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecips.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100" data-testid={`recip-row-${r.id}`}>
                    <td className="px-3 py-1.5 text-navy font-medium">{r.email}</td>
                    <td className="px-3 py-1.5 text-slate-600">{`${r.first_name || ''} ${r.last_name || ''}`.trim() || '—'}</td>
                    <td className="px-3 py-1.5">
                      <span className={`capitalize text-[10px] font-bold px-1.5 py-0.5 rounded ${['bounced', 'failed', 'complained'].includes(r.status) ? 'bg-rose-50 text-rose-600' : ['opened', 'clicked', 'delivered'].includes(r.status) ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{r.status}</span>
                    </td>
                    <td className="px-3 py-1.5 text-slate-500 hidden sm:table-cell">{(r.event_types || []).join(', ') || '—'}</td>
                  </tr>
                ))}
                {filteredRecips.length === 0 && <tr><td colSpan={4} className="px-3 py-4 text-center text-slate-400">No recipients</td></tr>}
              </tbody>
            </table>
          </div>
          {recips && recips.total > 25 && (
            <div className="flex items-center justify-center gap-3 mt-2 text-xs">
              <Button size="sm" variant="outline" disabled={rpage === 0} onClick={() => setRpage(rpage - 1)}><ChevronLeft className="w-3.5 h-3.5" /></Button>
              <span className="text-slate-500">Page {rpage + 1} of {Math.ceil(recips.total / 25)}</span>
              <Button size="sm" variant="outline" disabled={(rpage + 1) * 25 >= recips.total} onClick={() => setRpage(rpage + 1)}><ChevronRight className="w-3.5 h-3.5" /></Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
